import { useEffect, useState } from "hono/jsx";
import { BanItem } from "@/features/rooms/lib/bans.ts";

export const BanManager = () => {
  const [loading, setLoading] = useState(true);
  const [bans, setBans] = useState<
    { bannedIps: BanItem[]; bannedUsers: BanItem[] }
  >({ bannedIps: [], bannedUsers: [] });
  const [error, setError] = useState("");

  const fetchBans = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/rooms/bans");
      if (res.ok) {
        const data = await res.json();
        setBans(data);
      } else {
        setError("Failed to load bans");
      }
    } catch (e) {
      setError("Error connecting to server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBans();
  }, []);

  const removeBan = async (type: "ip" | "user", value: string) => {
    try {
      const res = await fetch(`/api/rooms/bans/${type}/${value}`, {
        method: "DELETE",
      });
      if (res.ok) {
        const data = await res.json();
        if (type === "ip") {
          setBans((prev) => ({ ...prev, bannedIps: data.bannedIps }));
        } else {
          setBans((prev) => ({ ...prev, bannedUsers: data.bannedUsers }));
        }
      }
    } catch (e) {
      console.error("Failed to remove ban", e);
    }
  };

  if (loading) {
    return (
      <div class="flex justify-center p-8">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  return (
    <div class="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {error && (
        <div class="alert alert-error shadow-lg">
          <span>{error}</span>
        </div>
      )}

      {/* Account Bans */}
      <div class="space-y-4">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-error/10 rounded-lg text-error">
            <span class="text-xl">👤</span>
          </div>
          <div>
            <h3 class="font-black uppercase tracking-tight text-lg">
              Account Bans
            </h3>
            <p class="text-xs opacity-50 uppercase font-bold tracking-wider">
              Prevents specific users from joining via their account
            </p>
          </div>
        </div>

        {bans.bannedUsers.length === 0
          ? (
            <div class="bg-base-200/50 rounded-xl p-8 text-center border-2 border-dashed border-base-300">
              <p class="opacity-40 italic font-medium">
                No accounts are currently banned.
              </p>
            </div>
          )
          : (
            <div class="grid gap-3">
              {bans.bannedUsers.map((ban) => (
                <div
                  key={ban.userId}
                  class="flex items-center justify-between p-4 bg-base-100 rounded-xl shadow-sm border border-base-300 hover:border-error/30 transition-colors group"
                >
                  <div class="flex flex-col">
                    <div class="flex items-center gap-2">
                      <span class="font-black text-lg">{ban.username}</span>
                      <span class="badge badge-error badge-xs font-black uppercase text-[10px] py-1.5 px-2">
                        Account
                      </span>
                    </div>
                    <div class="flex items-center gap-3 mt-1">
                      <span class="text-[10px] font-bold opacity-30 uppercase tracking-widest">
                        ID: {ban.userId}
                      </span>
                      <span class="text-[10px] font-bold opacity-30 uppercase tracking-widest">
                        •
                      </span>
                      <span class="text-[10px] font-bold opacity-30 uppercase tracking-widest">
                        {new Date(ban.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <div class="mt-2 text-[10px] font-bold bg-base-200 self-start px-2 py-0.5 rounded opacity-50 uppercase tracking-wider">
                      Last seen in: {ban.room}{" "}
                      ({ban.gameMode || "Unknown Mode"})
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const value = ban.userId || (ban as any).target; // Fallback for legacy
                      if (value) removeBan("user", value);
                    }}
                    class="btn btn-ghost btn-sm text-error opacity-0 group-hover:opacity-100 transition-opacity font-black uppercase text-xs"
                    disabled={!ban.userId && !(ban as any).target}
                  >
                    Unban
                  </button>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* IP Bans */}
      <div class="space-y-4">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-warning/10 rounded-lg text-warning">
            <span class="text-xl">🌐</span>
          </div>
          <div>
            <h3 class="font-black uppercase tracking-tight text-lg">
              IP Address Bans
            </h3>
            <p class="text-xs opacity-50 uppercase font-bold tracking-wider">
              Prevents any connection from these network addresses
            </p>
          </div>
        </div>

        {bans.bannedIps.length === 0
          ? (
            <div class="bg-base-200/50 rounded-xl p-8 text-center border-2 border-dashed border-base-300">
              <p class="opacity-40 italic font-medium">
                No IP addresses are currently banned.
              </p>
            </div>
          )
          : (
            <div class="grid gap-3">
              {bans.bannedIps.map((ban) => (
                <div
                  key={ban.ip}
                  class="flex items-center justify-between p-4 bg-base-100 rounded-xl shadow-sm border border-base-300 hover:border-warning/30 transition-colors group"
                >
                  <div class="flex flex-col">
                    <div class="flex items-center gap-2">
                      <span class="font-black text-lg font-mono tracking-tighter">
                        {ban.ip}
                      </span>
                      <span class="badge badge-warning badge-xs font-black uppercase text-[10px] py-1.5 px-2">
                        Network
                      </span>
                    </div>
                    <div class="flex items-center gap-3 mt-1">
                      <span class="text-[10px] font-bold opacity-30 uppercase tracking-widest">
                        Name: {ban.username}
                      </span>
                      <span class="text-[10px] font-bold opacity-30 uppercase tracking-widest">
                        •
                      </span>
                      <span class="text-[10px] font-bold opacity-30 uppercase tracking-widest">
                        {new Date(ban.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <div class="mt-2 text-[10px] font-bold bg-base-200 self-start px-2 py-0.5 rounded opacity-50 uppercase tracking-wider">
                      Last seen in: {ban.room}{" "}
                      ({ban.gameMode || "Unknown Mode"})
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const value = ban.ip || (ban as any).target; // Fallback for legacy
                      if (value) removeBan("ip", value);
                    }}
                    class="btn btn-ghost btn-sm text-error opacity-0 group-hover:opacity-100 transition-opacity font-black uppercase text-xs"
                    disabled={!ban.ip && !(ban as any).target}
                  >
                    Unban
                  </button>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};
